/**
 * Public connect page for one partner organization (TRUS-778 Phase 4).
 *
 * Referral emails to organizations that are not on WeaveIn link here
 * (/connect/<token>). The page shows what people near them are asking for
 * (counts only; the API hides any count under 11), the requests already sent
 * to them, how the referral flow works, and their listing: hours, who they
 * serve, the ZIPs they cover, languages, how they want requests, a weekly
 * maximum and a pause. The referral agent reads those settings. No login; the
 * token is the only key.
 */
import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Loader2, PauseCircle } from 'lucide-react';
import { config as productConfig } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';

const API_BASE = (typeof window !== 'undefined' ? (productConfig.weaveinApi.baseUrl ?? '') : '').trim().replace(/\/$/, '');

interface WaitingRequest {
  need?: string | null;
  need_label: string;
  zip?: string | null;
  language?: string | null;
  urgent: boolean;
  confirmed: boolean;
  respond_url: string;
}

interface Listing {
  hours?: string | null;
  populations?: string | null;
  service_zips: string[];
  languages: string[];
  preferred_channels: string[];
  weekly_cap?: number | null;
  paused: boolean;
  claimed: boolean;
  referral_form_url?: string | null;
}

interface ConnectData {
  organization: string;
  area: {
    total: number | null;
    spanish: number | null;
    waiting: number | null;
    top_need_label?: string | null;
    days: number;
    small_cell: number;
  };
  waiting: WaitingRequest[];
  listing: Listing;
  choices: { channels: string[]; weekly_caps: number[] };
}

export default function PartnerConnectPage({ token }: { token: string }) {
  const { t } = useAppTranslation();
  const [data, setData] = useState<ConnectData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Listing | null>(null);
  const [zipText, setZipText] = useState('');
  const [langText, setLangText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [claimName, setClaimName] = useState('');
  const [claimEmail, setClaimEmail] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/partner-connect/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'gone' : 'error');
        return res.json();
      })
      .then((json: ConnectData) => {
        if (cancelled) return;
        setData(json);
        setForm(json.listing);
        setZipText(json.listing.service_zips.join(', '));
        setLangText(json.listing.languages.join(', '));
        document.title = t('partnerConnect.docTitle', 'For {{organization}}', { organization: json.organization });
      })
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async (extra: Record<string, unknown> = {}) => {
    if (!form) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const res = await fetch(`${API_BASE}/api/public/partner-connect/${encodeURIComponent(token)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hours: form.hours || '',
          populations: form.populations || '',
          service_zips: zipText.split(/[\s,;]+/).filter(Boolean),
          languages: langText.split(',').map((s) => s.trim()).filter(Boolean),
          preferred_channels: form.preferred_channels,
          referral_form_url: (form.referral_form_url || '').trim() || null,
          weekly_cap: form.weekly_cap ?? null,
          paused: form.paused,
          ...extra,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || '');
      setForm(json.listing);
      setZipText(json.listing.service_zips.join(', '));
      setSaved(true);
    } catch (e: any) {
      setSaveError(e?.message || t('partnerConnect.saveError', "We couldn't save that just now. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="flex items-start gap-3 text-slate-700">
          <AlertCircle className="h-5 w-5 mt-0.5 text-slate-400" aria-hidden="true" />
          <p>
            {error === 'gone'
              ? t('partnerConnect.gone', 'This link is not valid. If you were expecting it, reply to our referral email and we will send a fresh one.')
              : t('partnerConnect.loadError', 'This page could not load right now. Please try again in a moment.')}
          </p>
        </div>
      </Shell>
    );
  }

  if (!data || !form) {
    return (
      <Shell>
        <p className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {t('partnerConnect.loading', 'Loading…')}
        </p>
      </Shell>
    );
  }

  const org = data.organization;
  const fewer = t('partnerConnect.fewerThan', 'Fewer than {{n}}', { n: data.area.small_cell });
  const stat = (n: number | null) => (n === null ? fewer : String(n));
  const toggleChannel = (ch: string) =>
    setForm({
      ...form,
      preferred_channels: form.preferred_channels.includes(ch)
        ? form.preferred_channels.filter((c) => c !== ch)
        : [...form.preferred_channels, ch],
    });
  const channelLabel = (ch: string) =>
    ch === 'email'
      ? t('partnerConnect.channelEmail', 'Email')
      : ch === 'fax'
        ? t('partnerConnect.channelFax', 'Fax')
        : ch === 'web_form'
          ? t('partnerConnect.channelWebForm', 'Our online form')
          : ch;

  return (
    <Shell>
      <header className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
          {t('partnerConnect.preparedFor', 'Prepared for {{organization}}', { organization: org })}
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight text-balance">
          {data.area.top_need_label
            ? t('partnerConnect.headlineNeed', 'People near {{organization}} are asking for {{need}} help.', {
                organization: org,
                need: data.area.top_need_label.toLowerCase(),
              })
            : t('partnerConnect.headline', 'People near {{organization}} are asking for help.', { organization: org })}
        </h1>
        <p className="text-slate-600 leading-relaxed max-w-prose">
          {t(
            'partnerConnect.intro',
            'WeaveIn helps people find local help and confirms what they need before sending them to you. You decide which requests to take.',
          )}
        </p>
      </header>

      <section className="mt-8 grid gap-3 sm:grid-cols-3">
        {[
          { n: data.area.total, label: t('partnerConnect.statTotal', 'requests near you, last {{days}} days', { days: data.area.days }) },
          { n: data.area.spanish, label: t('partnerConnect.statSpanish', 'of them in Spanish') },
          { n: data.area.waiting, label: t('partnerConnect.statWaiting', 'still waiting for a match') },
        ].map((s, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-2xl font-semibold text-slate-900">{stat(s.n)}</p>
            <p className="mt-1 text-sm text-slate-600">{s.label}</p>
          </div>
        ))}
        <p className="sm:col-span-3 text-xs text-slate-500">
          {t('partnerConnect.countsNote', 'Counts only. Any number under {{n}} is not shown.', { n: data.area.small_cell })}
        </p>
      </section>

      {data.waiting.length > 0 && (
        <section className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            {t('partnerConnect.waitingTitle', 'Requests waiting for you')}
          </h2>
          <div className="mt-3 space-y-3">
            {data.waiting.map((w, i) => (
              <article key={i} className="rounded-xl border border-teal-200 bg-white p-5">
                <h3 className="font-semibold text-slate-900">
                  {w.zip
                    ? t('partnerConnect.waitingNeedZip', 'Someone near {{zip}} needs: {{need}}', { zip: w.zip, need: w.need_label })
                    : t('partnerConnect.waitingNeed', 'Someone needs: {{need}}', { need: w.need_label })}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  {[
                    w.urgent ? t('partnerConnect.urgent', 'Urgent') : null,
                    w.language ? t('partnerConnect.prefers', 'Prefers {{language}}', { language: w.language }) : null,
                    w.confirmed ? t('partnerConnect.confirmed', 'Confirmed by text') : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t('partnerConnect.shareNote', 'Their first name and phone are shared once you accept. The first organization to accept takes the request.')}
                </p>
                <a
                  href={w.respond_url}
                  className="mt-3 inline-block rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800"
                >
                  {t('partnerConnect.respond', 'Accept or decline')}
                </a>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t('partnerConnect.howTitle', 'How it works for you')}</h2>
        <ol className="mt-3 grid gap-3 sm:grid-cols-2">
          {[
            [t('partnerConnect.how1Title', 'We confirm the need first'), t('partnerConnect.how1', 'Our agent, Wren, texts the person and checks what they need, where, and in which language.')],
            [t('partnerConnect.how2Title', 'You get a short request'), t('partnerConnect.how2', 'By email or fax, your choice. Accept with one tap or a YES reply.')],
            [t('partnerConnect.how3Title', 'We hand them to you'), t('partnerConnect.how3', 'With their consent, you get their name and number, and they get yours and what to bring.')],
            [t('partnerConnect.how4Title', 'We check in after'), t('partnerConnect.how4', 'On day 3 we ask the person if they got help, so you see your outcomes.')],
          ].map(([title, body], i) => (
            <li key={i} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="font-semibold text-slate-900">
                {i + 1}. {title}
              </p>
              <p className="mt-1 text-sm text-slate-600">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-10 rounded-xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4">
        <div>
          <h2 className="font-semibold text-slate-900">{t('partnerConnect.listingTitle', 'Is your listing right?')}</h2>
          <p className="text-sm text-slate-600">
            {t('partnerConnect.listingIntro', 'Correct it so we send you the right people.')}
          </p>
        </div>
        <Field label={t('partnerConnect.hours', 'Hours')}>
          <input className={INPUT} value={form.hours || ''} onChange={(e) => setForm({ ...form, hours: e.target.value })} />
        </Field>
        <Field label={t('partnerConnect.populations', 'Who you serve')}>
          <textarea className={INPUT} rows={2} value={form.populations || ''} onChange={(e) => setForm({ ...form, populations: e.target.value })} />
        </Field>
        <Field label={t('partnerConnect.zips', 'ZIP codes you cover')} hint={t('partnerConnect.zipsHint', 'Separate with commas. Leave empty to get requests from near your address.')}>
          <input className={INPUT} value={zipText} onChange={(e) => setZipText(e.target.value)} />
        </Field>
        <Field
          label={t('partnerConnect.formUrl', 'Your online referral or intake form')}
          hint={t('partnerConnect.formUrlHint', 'If you take referrals through a web form, paste its link. A WeaveIn navigator fills it in for each person.')}
        >
          <input
            className={INPUT}
            type="url"
            placeholder="https://"
            value={form.referral_form_url || ''}
            onChange={(e) => setForm({ ...form, referral_form_url: e.target.value })}
          />
        </Field>
        <Field label={t('partnerConnect.languages', 'Languages')} hint={t('partnerConnect.languagesHint', 'Separate with commas.')}>
          <input className={INPUT} value={langText} onChange={(e) => setLangText(e.target.value)} />
        </Field>
        <fieldset>
          <legend className="text-sm font-medium text-slate-800">{t('partnerConnect.channels', 'Send requests by')}</legend>
          <div className="mt-2 flex flex-wrap gap-4">
            {data.choices.channels.map((ch) => (
              <label key={ch} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.preferred_channels.includes(ch)} onChange={() => toggleChannel(ch)} />
                {channelLabel(ch)}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="text-sm font-medium text-slate-800">{t('partnerConnect.cap', 'Most requests per week')}</legend>
          <div className="mt-2 flex flex-wrap gap-4">
            {[...data.choices.weekly_caps, null].map((cap) => (
              <label key={String(cap)} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="weekly_cap"
                  checked={(form.weekly_cap ?? null) === cap}
                  onChange={() => setForm({ ...form, weekly_cap: cap })}
                />
                {cap === null ? t('partnerConnect.noLimit', 'No limit') : cap}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.paused} onChange={(e) => setForm({ ...form, paused: e.target.checked })} />
          <PauseCircle className="h-4 w-4 text-slate-500" aria-hidden="true" />
          {t('partnerConnect.pause', 'Pause new requests for now')}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => save()}
            disabled={saving}
            className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-60"
          >
            {saving && <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />}
            {t('partnerConnect.save', 'Save my listing')}
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-teal-700">
              <CheckCircle className="h-4 w-4" aria-hidden="true" /> {t('partnerConnect.saved', 'Saved. The agent will follow these settings.')}
            </span>
          )}
          {saveError && <span className="text-sm text-red-700">{saveError}</span>}
        </div>
      </section>

      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 sm:p-6 space-y-3">
        {form.claimed ? (
          <p className="flex items-center gap-2 text-slate-700">
            <CheckCircle className="h-5 w-5 text-teal-600" aria-hidden="true" />
            {t('partnerConnect.claimedDone', 'This page is claimed. Use this link any time to update your settings.')}
          </p>
        ) : (
          <>
            <h2 className="font-semibold text-slate-900">{t('partnerConnect.claimTitle', 'Claim your free WeaveIn page')}</h2>
            <p className="text-sm text-slate-600">
              {t('partnerConnect.claimIntro', 'Tell us who keeps this listing current. We contact you only about requests and your listing.')}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={INPUT} placeholder={t('partnerConnect.claimName', 'Your name')} value={claimName} onChange={(e) => setClaimName(e.target.value)} />
              <input className={INPUT} type="email" placeholder={t('partnerConnect.claimEmail', 'Work email')} value={claimEmail} onChange={(e) => setClaimEmail(e.target.value)} />
            </div>
            <button
              type="button"
              onClick={() => save({ claim: { name: claimName.trim(), email: claimEmail.trim() } })}
              disabled={saving || !claimName.trim() || !claimEmail.includes('@')}
              className="rounded-lg border border-teal-700 px-4 py-2 text-sm font-medium text-teal-800 hover:bg-teal-50 disabled:opacity-60"
            >
              {t('partnerConnect.claim', 'Claim my page')}
            </button>
          </>
        )}
      </section>
    </Shell>
  );
}

const INPUT = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-600 focus:outline-none';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-800">{label}</span>
      {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      <span className="mt-1 block">{children}</span>
    </label>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">{children}</main>
    </div>
  );
}
